import { useEffect, useMemo } from "react";

import {
  type AWSNodeConfigApiParameters,
  type Member,
  type StructureApiServiceModelDescriptor,
} from "@doitintl/cmp-models";
import { Alert, Autocomplete, FormControl, Link, MenuItem, TextField, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import { useFormikContext } from "formik";
import isEqual from "lodash/isEqual";
import noop from "lodash/noop";

import { cloudflowTexts } from "../../../../assets/texts";
import { useCustomerId } from "../../../../Components/hooks/useCustomerId";
import { AccountStatus } from "../../../../Pages/Settings/AWS/types";
import { useAWSAccounts } from "../../../Settings/AWS/hooks";
import { ALL_ACCOUNTS } from "../../consts";
import { useAwsData } from "../../hooks";
import { useAWSCloudPermissions } from "../ConfigurationPanel/hooks";
import { withRootPath } from "./CloudSpecificParameterForm";
import { useFieldCommonProps } from "./useFieldCommonProps";
import { useInheritedConfigurationValues } from "./useInheritedConfigurationValues";

const ALL_REGIONS = "All regions";

const allAccountsOption = {
  accountId: ALL_ACCOUNTS,
  status: AccountStatus.healthy,
  properties: {
    name: "",
  },
};

const AWSParameterForm = ({
  inputModel,
  isOperationInputPresent,
  rootPath,
}: {
  inputModel: StructureApiServiceModelDescriptor<Member>;
  isOperationInputPresent: boolean;
  rootPath?: string;
}) => {
  const customerId = useCustomerId();
  const [accounts] = useAWSAccounts();
  const { getFieldProps, setFieldValue } = useFormikContext<{ accountId: string; regions: string[] }>();

  const inheritedConfigurationValues = useInheritedConfigurationValues(
    (actionNodeParameters: AWSNodeConfigApiParameters) => Boolean(actionNodeParameters.configurationValues.accountId)
  );

  const { updatePermissions } = useAWSCloudPermissions();

  const accountIdProps = useFieldCommonProps<string>(
    getFieldProps(withRootPath("accountId", rootPath)),
    "Account",
    true
  );
  const regionsProps = useFieldCommonProps(getFieldProps(withRootPath("regions", rootPath)), "Regions", false);

  const shouldShowRegionSelector = "regions" in inputModel.members;

  const { regions } = useAwsData(accountIdProps.value, shouldShowRegionSelector && Boolean(accountIdProps.value));

  const accountOptions = useMemo(() => [allAccountsOption, ...accounts], [accounts]);

  const selectedAccount = useMemo(
    () => accountOptions.find((account) => account.accountId === accountIdProps.value) || null,
    [accountOptions, accountIdProps]
  );

  const showWarning = selectedAccount?.status === AccountStatus.notConfigured;

  const shouldOverrideAccountSelector = Boolean(inheritedConfigurationValues?.configurationValues.accountId);
  const shouldOverrideRegionSelector = Boolean(inheritedConfigurationValues?.configurationValues.regions);

  useEffect(() => {
    const inheritedValues = inheritedConfigurationValues?.configurationValues;
    if (!inheritedValues) return;

    if (shouldOverrideAccountSelector && accountIdProps.value !== inheritedValues.accountId) {
      setFieldValue(accountIdProps.name, inheritedValues.accountId);
    }
    if (shouldOverrideRegionSelector && !isEqual(regionsProps.value, inheritedValues.regions)) {
      setFieldValue(regionsProps.name, inheritedValues.regions);
    }
  }, [
    shouldOverrideAccountSelector,
    shouldOverrideRegionSelector,
    inheritedConfigurationValues?.configurationValues,
    setFieldValue,
    accountIdProps,
    regionsProps,
    updatePermissions,
  ]);

  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      {isOperationInputPresent && (
        <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
          Parameters
        </Typography>
      )}

      <FormControl>
        <Autocomplete
          options={accountOptions}
          groupBy={(option) =>
            option.status === AccountStatus.notConfigured ? "Unlinked accounts" : "Linked accounts"
          }
          getOptionLabel={(option) =>
            option.properties?.name ? `${option.accountId} (${option.properties.name})` : option.accountId
          }
          renderOption={(props, option) => (
            <MenuItem {...props} key={option.accountId} value={option.accountId}>
              {option.properties?.name ? `${option.accountId} (${option.properties.name})` : option.accountId}
            </MenuItem>
          )}
          onChange={(_, value) => {
            if (value?.accountId) {
              updatePermissions(value?.accountId);
            }
            setFieldValue(accountIdProps.name, value?.accountId);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              {...accountIdProps}
              type="text"
              fullWidth
              variant="outlined"
              onChange={(e) => {
                const matchingAccount = accountOptions.find((account) => account.accountId === e.target.value);
                if (matchingAccount) {
                  updatePermissions(matchingAccount.accountId);
                  setFieldValue(accountIdProps.name, matchingAccount.accountId);
                }
              }}
            />
          )}
          size="small"
          fullWidth
          value={selectedAccount}
          disabled={shouldOverrideAccountSelector}
        />
      </FormControl>
      {showWarning && (
        <Alert severity="warning">
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography
              variant="body2"
              sx={{
                fontWeight: 400,
              }}
              flexShrink={1}
            >
              {cloudflowTexts.CONNECT_AWS_ACCOUNT_WARNING}
            </Typography>
            <Link
              href={`/customers/${customerId}/integrate/aws/link`}
              target="_blank"
              underline="hover"
              color="inherit"
              flexShrink={0}
            >
              Link account
            </Link>
          </Stack>
        </Alert>
      )}
      {shouldShowRegionSelector && (
        <FormControl>
          <Autocomplete
            multiple
            options={regions}
            onChange={async (_, value) => setFieldValue(regionsProps.name, value)}
            renderOption={(props, option) => (
              <MenuItem {...props} value={option}>
                {option}
              </MenuItem>
            )}
            disableCloseOnSelect
            getOptionDisabled={(option) => regionsProps.value?.includes(ALL_REGIONS) && option !== ALL_REGIONS}
            disabled={regions.length === 0 || shouldOverrideRegionSelector}
            filterSelectedOptions
            renderInput={(params) => {
              const { InputProps, ...rest } = params;
              if (
                (!Array.isArray(regionsProps.value) || regionsProps.value.length === 0) &&
                !rest.inputProps["aria-expanded"] &&
                !rest.inputProps.value
              ) {
                InputProps.startAdornment = (
                  <Typography
                    sx={{
                      pl: 1,
                    }}
                  >
                    {ALL_REGIONS}
                  </Typography>
                );
              }
              return (
                <TextField
                  {...rest}
                  {...regionsProps}
                  onChange={noop}
                  fullWidth
                  variant="outlined"
                  slotProps={{
                    input: InputProps,
                  }}
                />
              );
            }}
            size="small"
            fullWidth
            value={regionsProps.value ?? []}
          />
        </FormControl>
      )}
      {inheritedConfigurationValues && (
        <Alert severity="info" data-cy="aws-inherited-values-warning">
          <b>Account</b>
          {shouldShowRegionSelector && shouldOverrideRegionSelector && (
            <>
              {" and "} <b>Region</b>
            </>
          )}
          {" inherited from "}
          <b>
            {inheritedConfigurationValues.stepNumber}.&nbsp;{inheritedConfigurationValues.name}
          </b>
          .
        </Alert>
      )}
    </Stack>
  );
};

export default AWSParameterForm;
